import { FlashList } from '@shopify/flash-list';
import { useRouter } from 'expo-router';
import React, { useEffect, useMemo } from 'react';
import { Dimensions, View } from 'react-native';
import { createStyleSheet, useStyles } from 'react-native-unistyles';

import { usePaginatedLocations } from '@/api/partners/locations/use-paginated-locations';
import { NoCarsEmptyState } from '@/components/first-car-empty-state';
import { MapView } from '@/components/map-view';
import { useAuth } from '@/core';
import mixpanelService from '@/core/clients/mixpanel';
import { useDebounce } from '@/core/hooks/use-debounced-value';
import { Button, Input, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Search } from '@/ui/icons';
import { FlagFilled } from '@/ui/icons/flag-filled';
import { Marker } from '@/ui/icons/marker';
import { ListItem } from '@/ui/list-item';
import { Loader } from '@/ui/loader';

const PAGE_HEIGHT = Dimensions.get('window').height;

export default function Locations() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const status = useAuth.use.status();
  const [text, setText] = React.useState<string>('');
  const debouncedText = useDebounce(text, 400);

  const {
    data,
    isFetchingNextPage,
    fetchNextPage,
    isLoading,
    isPending,
    hasNextPage,
    refetch: refetchLocations,
    isSuccess,
  } = usePaginatedLocations({
    enabled: status === 'signIn',
    text: debouncedText,
  });

  useEffect(() => {
    mixpanelService.track('Page Viewed', {
      'Page Name': 'Partner Locations',
    });
  }, []);

  const locations = useMemo(() => {
    return data?.pages.map((page) => page.data).flat();
  }, [data]);
  const hasLocations = (locations?.length ?? 0) > 0;
  const hasActiveSearch = debouncedText.trim().length > 0;

  const markers = React.useMemo(() => {
    if (!locations) {
      return [];
    }

    return locations.map((loc) => ({
      latitude: loc.latitude,
      longitude: loc.longitude,
      icon: (
        <Icon
          type="naked"
          icon={<FlagFilled />}
          fillColor={theme.colors.icon.nakedColor}
          width={14}
          height={14}
        />
      ),
      active: true,
    }));
  }, [locations]);

  if (isPending && !data) {
    return (
      <View
        style={{
          width: '100%',
          height: '100%',
          justifyContent: 'center',
          alignItems: 'center',
          backgroundColor: theme.colors.background_1,
        }}
      >
        <Loader style={{ height: 250, width: 250 }} />
      </View>
    );
  }

  if (!hasLocations && isSuccess) {
    return (
      <NoCarsEmptyState
        title="No Locations Yet"
        body="Add your first location to get started"
        ctaText="Add Location"
        illustration={
          <View style={{ alignItems: 'center' }}>
            <Icon
              type="dressed"
              icon={<Marker />}
              fillColor={theme.colors.icon.color}
              width={18}
              height={18}
              style={{
                borderRadius: 28,
              }}
            />
          </View>
        }
        onPress={() => {
          mixpanelService.track('Button Clicked', {
            'Button Name': 'Add Location',
          });
          router.push('/partner/more/locations/add-location');
        }}
      />
    );
  }

  if (
    isSuccess &&
    !hasLocations &&
    !hasActiveSearch &&
    (locations?.length ?? 0) === 0
  ) {
    return (
      <View
        style={{
          flex: 1,
          backgroundColor: theme.colors.background_1,
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <View style={styles.emptyStateContainer}>
          <View style={{ alignItems: 'center' }}>
            <Icon
              type="dressed"
              icon={<Marker />}
              fillColor={theme.colors.icon.color}
              width={18}
              height={18}
              style={{
                borderRadius: 28,
              }}
            />
          </View>

          <View style={{ alignItems: 'center', gap: 8 }}>
            <Text type="title2">No Locations Yet</Text>

            <Text
              type="bodyRegular"
              style={{
                color: theme.colors.text.secondary,
                textAlign: 'center',
              }}
            >
              Add your first location to get started
            </Text>
          </View>

          <View>
            <Button
              text="Add Location"
              type="primary"
              onPress={() => {
                mixpanelService.track('Button Clicked', {
                  'Button Name': 'Add Location',
                });
                router.push('/partner/more/locations/add-location');
              }}
            />
          </View>
        </View>
      </View>
    );
  }

  return (
    <View
      style={{
        flex: 1,
        backgroundColor: theme.colors.background_1,
        maxHeight: PAGE_HEIGHT,
      }}
    >
      <View style={styles.container}>
        <View style={styles.header}>
          <View style={{ flex: 1 }}>
            <Text type="title1">Locations</Text>

            <Text
              type="bodyRegular"
              style={{ color: theme.colors.text.secondary }}
            >
              Manage your locations and pickup dropoff notes.
            </Text>
          </View>

          <View>
            <Button
              text="Add Location"
              type="primary"
              onPress={() => {
                router.push('/partner/more/locations/add-location');
              }}
              style={{ width: 150 }}
            />
          </View>
        </View>

        <View style={styles.body}>
          <View style={styles.inputMobile}>
            <Input
              placeholder="Search by address"
              value={text}
              onChangeText={(t: string) => setText(t)}
              leftIcon={
                <Icon
                  type="naked"
                  icon={<Search />}
                  fillColor={theme.colors.icon.color}
                  width={22}
                  height={22}
                />
              }
            />
          </View>

          <View style={styles.mapView}>
            <MapView
              polylines={[]}
              markers={markers}
              containerStyle={{ borderRadius: 16 }}
              fitBounds
              altitude={15000}
              zoom={3}
              scrollEnabled={false}
            />
          </View>

          <View style={styles.inputDesktop}>
            <Input
              placeholder="Search by address"
              value={text}
              onChangeText={(t: string) => setText(t)}
              leftIcon={
                <Icon
                  type="naked"
                  icon={<Search />}
                  fillColor={theme.colors.icon.color}
                  width={22}
                  height={22}
                />
              }
            />
          </View>

          <View style={{ flex: 1 }}>
            <FlashList
              data={locations}
              renderItem={({ item }) => (
                <ListItem
                  style={{ flexGrow: 1 }}
                  text={item.address}
                  caption={`${item.carsCount} cars in this location`}
                  icon={
                    <Icon
                      type="dressed"
                      icon={<Marker />}
                      fillColor={theme.colors.icon.color}
                      width={25}
                      height={25}
                    />
                  }
                  onPress={() => {}}
                  withBorder={false}
                  button={
                    <Button
                      text="Edit"
                      type="secondaryExtraSmall"
                      onPress={() => {
                        router.push(`/partner/more/locations/${item.id}`);
                      }}
                      style={{ width: 60 }}
                    />
                  }
                />
              )}
              ItemSeparatorComponent={() => <View style={{ height: 16 }} />}
              onEndReached={() => {
                if (hasNextPage && !isFetchingNextPage) {
                  console.log('end reached');
                  fetchNextPage();
                }
              }}
              onRefresh={async () => {
                await refetchLocations();
              }}
              refreshing={isLoading}
              estimatedItemSize={20}
              showsVerticalScrollIndicator
              contentContainerStyle={{
                paddingRight: 16,
              }}
              ListFooterComponent={() => {
                if (!isFetchingNextPage) return null;

                return <Loader style={{ height: 140 }} />;
              }}
            />
          </View>

          <View style={styles.addLocationMobile}>
            <Button
              text="Add Location"
              type="primary"
              onPress={() => {
                router.push('/partner/more/locations/add-location');
              }}
              style={{ width: '100%' }}
            />
          </View>
        </View>
      </View>
    </View>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  emptyStateContainer: {
    flex: 1,
    gap: 16,
    padding: 16,
    justifyContent: 'center',
    width: {
      xs: '100%',
      md: 400,
    },
  },
  container: {
    flexGrow: 1,
    paddingTop: {
      xs: 24,
      md: 56,
    },
    paddingLeft: {
      xs: 16,
      md: 56,
    },
    paddingRight: {
      xs: 16,
      md: 56,
    },
    paddingBottom: {
      md: 56,
    },
    gap: {
      xs: 16,
      md: 64,
    },
    maxWidth: {
      xs: '100%',
      sm: 600,
      md: '100%',
    },
    width: {
      xs: '100%',
      md: '100%',
    },
    maxHeight: '100%',
  },
  header: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  body: {
    flex: 1,
    gap: {
      xs: 16,
      md: 32,
    },
  },
  inputMobile: {
    display: {
      xs: 'flex',
      md: 'none',
    },
  },
  inputDesktop: {
    display: {
      xs: 'none',
      md: 'flex',
    },
    width: 350,
  },
  mapView: {
    width: '100%',
    height: 250,
    borderRadius: 16,
    overflow: 'hidden',
  },
  addLocationMobile: {
    display: {
      xs: 'flex',
      md: 'none',
    },
    flexDirection: 'row',
  },
}));
